import {styled} from '@linaria/react';
import React, {useCallback, useEffect, useRef} from 'react';
import {COLORS} from '../../../constants/colors';
import {
  mediaMobileOnly,
  tabletAndDownLinaria,
} from '../../../constants/customMedia';
import {UnstyledButton} from '../../atoms/Buttons/UnstyledButton';
import {SliderPaginationDots} from '../../organisms/SliderPaginationDots/SliderPaginationDots';
import {HorizontalList} from '../HorizontalList/HorizontalList';
import {useSlider} from '../HorizontalSlider/hooks/useSlider';

const MIN_SCALE = 0.95;
const SCALE_TRANSITION_MS = 220;
const PEEK_GAP = 8;
const PEEK_OFFSET = 10;
const PEEK_TOTAL = PEEK_OFFSET * 2 + PEEK_GAP * 2;

export interface BaseSliderProps<T> {
  items: T[];
  getItemKey: (item: T) => string;
  renderSlide: (item: T, index: number) => React.ReactNode;
  className?: string;
  hideButtonControls?: boolean;
  initialSlideIndex?: number;
  dataTestId?: string;
  prevArrowAriaLabel?: string;
  nextArrowAriaLabel?: string;
}

export const BaseSlider = <T,>({
  items,
  getItemKey,
  renderSlide,
  className,
  hideButtonControls = false,
  initialSlideIndex = 0,
  dataTestId = 'baseSlider',
  prevArrowAriaLabel = 'Go to previous slide',
  nextArrowAriaLabel = 'Go to next slide',
}: BaseSliderProps<T>) => {
  const sliderRef = useRef<HTMLElement | null>(null);
  const shouldPeekSiblingSlides = items.length > 1;

  const {
    currentSlide: activeSlide,
    prevIsActive: hasPrev,
    nextIsActive: hasNext,
    scrollToSlide,
  } = useSlider(sliderRef, {padding: 0});

  const goToSlideWithBehavior = useCallback(
    (index: number, behavior: 'auto' | 'smooth' = 'smooth') => {
      scrollToSlide(index, {
        behavior,
        centered: shouldPeekSiblingSlides,
      });
    },
    [scrollToSlide, shouldPeekSiblingSlides],
  );

  const goToSlide = useCallback(
    (index: number) => {
      goToSlideWithBehavior(index);
    },
    [goToSlideWithBehavior],
  );

  useEffect(() => {
    if (!shouldPeekSiblingSlides || initialSlideIndex === 0) {
      return;
    }
    goToSlideWithBehavior(initialSlideIndex, 'auto');
  }, [goToSlideWithBehavior, shouldPeekSiblingSlides, initialSlideIndex]);

  if (items.length === 0) {
    return null;
  }

  return (
    <SliderWrapper
      data-testid={dataTestId}
      $addPaddingRight={items.length === 1}
      className={className}
    >
      <StyledHorizontalList ref={sliderRef} disableFade>
        {items.map((item, index) => (
          <Slide
            key={getItemKey(item)}
            data-slide-index={index}
            data-active={activeSlide === index}
            $peek={shouldPeekSiblingSlides}
          >
            {renderSlide(item, index)}
          </Slide>
        ))}
      </StyledHorizontalList>
      {items.length > 1 && !hideButtonControls && (
        <>
          <SlideButton
            type="button"
            data-direction="left"
            data-visible={hasPrev}
            aria-label={prevArrowAriaLabel}
            disabled={!hasPrev}
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              if (hasPrev) {
                goToSlide(activeSlide - 1);
              }
            }}
          >
            {'<'}
          </SlideButton>
          <SlideButton
            type="button"
            data-direction="right"
            data-visible={hasNext}
            aria-label={nextArrowAriaLabel}
            disabled={!hasNext}
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              if (hasNext) {
                goToSlide(activeSlide + 1);
              }
            }}
          >
            {'>'}
          </SlideButton>
        </>
      )}
      {items.length > 1 && (
        <StyledSliderPaginationDots
          data-testid={`${dataTestId}Pagination`}
          currentSlide={activeSlide}
          maxPages={items.length}
          scrollToSlide={goToSlide}
        />
      )}
    </SliderWrapper>
  );
};

const SliderWrapper = styled.div<{$addPaddingRight?: boolean}>`
  margin-top: 8px;
  padding-left: 8px;
  padding-right: 8px;
  position: relative;
  padding-bottom: 12px;

  @media ${mediaMobileOnly} {
    padding-left: 1rem;
    padding-right: 1rem;
  }
`;

const StyledHorizontalList = styled(HorizontalList)`
  display: flex;
  & > div {
    scroll-snap-type: x mandatory;
  }
`;

const Slide = styled.div<{$peek: boolean; $reducePeekTotal?: boolean}>`
  flex: 0 0 ${({$peek}) => ($peek ? `calc(100% - ${PEEK_TOTAL}px)` : '100%')};
  scroll-snap-align: ${({$peek}) => ($peek ? 'center' : 'start')};
  max-width: 376px; // added here because of tablet view

  @keyframes slide-scale-flow {
    0%,
    100% {
      transform: scale(${MIN_SCALE});
    }
    50% {
      transform: scale(1);
    }
  }

  @supports (view-timeline-axis: inline) {
    --timeline-name: --slide-scale;
    view-timeline: var(--timeline-name);
    view-timeline-axis: inline;
    will-change: transform;
    animation: slide-scale-flow both linear;
    animation-timeline: var(--timeline-name);
    animation-duration: 1ms;
  }

  @supports not (view-timeline-axis: inline) {
    transform: scale(${MIN_SCALE});
    transition: transform ${SCALE_TRANSITION_MS}ms ease-out;
    will-change: transform;

    &[data-active='true'] {
      transform: scale(1);
    }

    &[data-active='false'] {
      transform: scale(${MIN_SCALE});
    }
  }
`;

const StyledSliderPaginationDots = styled(SliderPaginationDots)`
  margin-top: 8px;
  padding-bottom: 0;

  &[data-is-desktop='true'] {
    ${tabletAndDownLinaria(`display: none;`)}
  }
`;

const SlideButton = styled(UnstyledButton)`
  position: absolute;
  top: 42%;
  transform: translateY(-50%);
  z-index: 1;
  width: 24px;
  height: 24px;
  border-radius: 100px;
  background: ${COLORS.White};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.1);

  ${tabletAndDownLinaria(`display: none;`)}

  &[data-direction='left'] {
    left: 4px;

    svg {
      transform: rotate(180deg);
    }
  }

  &[data-direction='right'] {
    right: 4px;
  }

  &:disabled {
    opacity: 0.4;
  }

  &[data-visible='false'] {
    opacity: 0;
    pointer-events: none;
  }
`;
